import Tooltip from "@shared/v2/Tooltip";
import IconButton from "@shared/v2/IconButton";
import { PhoneSolidV6, UserPlusSolidV6 } from "@shared/v2/Icomoon";
import { getAddressAttributes } from "@shared/v2/HandwrittenCard";
import MarketerOnboardingTooltip from "@shared/v2/MarketerOnboardingTooltip";
import SmsModalButton from "../../SmsModal/components/SmsModalButton";
import TaskFormLogAppointment from "../../Tasks/components/TaskFormLogAppointment";
import OtherInteract from "../../People/components/OtherInteract";
import LastInteractions from "../components/LastInteractions";
import NewTask from "../../Tasks/components/NewTask";
import AgentOrPondSelect from "../../shared/AgentOrPondSelect";
import AssignedPlansPreviewApp from "../../AssignedPlans/startup/AssignedPlansPreviewApp";
import MilestoneDate from "../components/MilestoneDate";
import HomeAppLink from "../../HomeApp/components/HomeAppLink";
import FavoritesOrViews from "../../People/components/FavoritesOrViews";
import AllListingAlertPreview from "../../PersonDetail/components/listing_alerts/AllListingAlertPreview";
import MarketReportPreview from "../../MarketReports/MarketReportPreview";
import Status from "../components/Status";
import TagTooltip from "../components/TagTooltip";
import CMALink from "../components/CMALink";
import EmailInteraction from "../components/EmailInteraction";
import Intent from "../components/Intent";
import Name from "../components/Name";
import Address from "../components/Address";
import Stage from "../components/Stage";
import SendMailMenu from "../../Tasks/components/SendMailMenu";

const cellRenderers = ({
  person,
  currentUser,
  account,
  platformAccounts,
  source,
  featureFlags,
  isUSAccount,
  isSendMailDisabled,
  isOnboarded,
  messagingServiceUrl,
  idxDataUri,
  idxDataToken,
  setCallParams,
  primaryPhone,
  handleSelectRows,
  hasIdxInfo,
  lastVisit,
  Visits,
  leadPond,
  primaryAgent,
  setLeadPond,
  setPrimaryAgent,
  setLeadMatch,
  setEditPersonId,
  setMarketModal,
  editMarketReport,
  deleteMarketReport,
  onToggleCollectionNotification,
}) => {
  const widths = {
    minWidth: "210px",
    maxWidth: "210px",
  };

  const getSendMailTooltip = () => {
    if (!isSendMailDisabled) return "Send Mail";

    if (!isOnboarded) {
      return <MarketerOnboardingTooltip />;
    }

    const hasAddress =
      getAddressAttributes({ ...person.person, included: person.addresses?.data }).length > 0;
    const needsFirstName = featureFlags.sendHandwrittenCard && !person.person.first_name?.trim();

    if (hasAddress || !needsFirstName) return "Contact must have a valid address to be sent mail";
    return "Contact must have a first name and a valid address to be sent mail";
  };

  return [
    [
      "checkbox",
      <td key="checkbox" className="checkbox-col fixed-col">
        <div className="checkboxselect checkbox lead-checkbox">
          {person.edit_permission && (
            <>
              <input
                type="checkbox"
                name={person.multi_select_id}
                onChange={handleSelectRows}
                id={person.multi_select_id}
                value="1"
                className="multi-select"
              />
              <label
                aria-label={person.multi_select_id}
                htmlFor={person.multi_select_id}
                className="tw-pt-2px"
              />
            </>
          )}
        </div>
      </td>,
    ],
    [
      "name",
      <td key="name" className="name-search-result-count fixed-col" aria-label="name">
        <Name person={person} setLeadMatch={setLeadMatch} />
      </td>,
    ],
    [
      "showInLeads",
      source === "leads" ? (
        <td key="showInLeads" className="finish-lead">
          {person.is_partial && (
            <IconButton schema="transparent">
              <UserPlusSolidV6 size="l" onClick={() => setEditPersonId(person.id)} />
            </IconButton>
          )}
        </td>
      ) : (
        <td key="showInLeads" className="display-in-leads">
          <div className="display-in-lead">
            <i
              aria-label="display in leads"
              className={`fa  ${person.person.is_lead ? "fa-eye brivity-blue" : "fa-eye-slash"}`}
              data-display-in-leads={person.person.is_lead !== undefined}
            />
          </div>
        </td>
      ),
    ],
    [
      "intent",
      <td key="intent" className="intent" aria-label="intent">
        <Intent person={person} />
      </td>,
    ],
    [
      "status",
      <td key="status" className="tw-min-w-[150px] tw-w-[150px] status-header" aria-label="status">
        <Status person={person} />
      </td>,
    ],
    [
      "stage",
      <td key="stage" className="tw-min-w-[150px] tw-w-[150px]" aria-label="stage">
        <Stage person={person} />
      </td>,
    ],
    [
      "email",
      <td key="email" className="interaction-icon" aria-label="email">
        <EmailInteraction
          personDetails={person.person_smsbutton_serializable_hash}
          currentUserId={currentUser.id}
          person={person}
          emailDetails={person.email_details}
          unsubscribedMessageTypes={person.unsubscribed_message_types}
        />
      </td>,
    ],
    [
      "phone",
      <td key="phone" className="interaction-icon" aria-label="call">
        <Tooltip
          placement="right"
          content={person.primary_phone_number}
          trigger={
            <IconButton
              onClick={() => setCallParams({ leadUuids: [person.person.uuid], callSource: "Lead Index" })}
              disabled={!person.primary_phone_number}
            >
              <PhoneSolidV6 />
            </IconButton>
          }
        />
      </td>,
    ],
    [
      "sms",
      <td key="sms" className="interaction-icon" aria-label="send sms">
        <SmsModalButton
          gabbiAi={featureFlags.gabbiAi}
          messagingServiceUrl={messagingServiceUrl}
          smsEnabled={person.primary_phone_number > "" && currentUser.managedPhoneNumber !== undefined}
          person={person.person_smsbutton_serializable_hash}
          phone={primaryPhone}
        />
      </td>,
    ],
    [
      "appointment",
      <td key="appointment" className="interaction-icon" aria-label="schedule appointment">
        <Tooltip
          placement="right"
          content="Schedule Appointment"
          trigger={
            <TaskFormLogAppointment person={person.person} interaction={person.appointment_interaction} />
          }
        />
      </td>,
    ],
    isUSAccount && [
      "mail",
      <td key="mail" className="interaction-icon" aria-label="Send Mail">
        <Tooltip
          placement="right"
          innerClassName={isOnboarded ? "tw-max-w-[180px]" : "tw-max-w-[250px]"}
          multiline
          content={getSendMailTooltip()}
          trigger={
            <SendMailMenu
              isSendMailDisabled={isSendMailDisabled}
              showHandwrittenCard={featureFlags.sendHandwrittenCard}
              person={{
                ...person.person,
                primary_agent_uuid: person.primaryAgent?.uuid,
                direct_url: person.direct_url,
                included: person.addresses?.data,
              }}
            />
          }
        />
      </td>,
    ],
    [
      "other",
      <td key="other" className="interaction-icon" aria-label="other">
        <OtherInteract
          otherInteract={{
            person: person.person,
            personAvatar: person.personAvatar,
            personFirstName: person.person.first_name,
            personLastName: person.person.last_name,
            interaction: person.other_interaction,
          }}
        />
      </td>,
    ],
    [
      "address",
      <td key="address" className="address" style={widths} aria-label="address">
        <Address
          streetAddress={`${person.display_a_street_address}${person.display_a_secondary_address ? `, ${person.display_a_secondary_address}` : ""}`}
          cityState={person.display_city_state_and_zip}
        />
      </td>,
    ],
    [
      "lastInteraction",
      <td key="lastInteraction" className="last-interaction text-center" aria-label="last interaction">
        <LastInteractions
          interactionTypeString={person.interaction_type_string}
          mostRecentInteractionTime={person.most_recent_interaction_time}
        />
      </td>,
    ],
    [
      "nextDueTasks",
      <td key="nextDueTasks" className="next-due-tasks" aria-label="next due task">
        <NewTask
          user={currentUser}
          buttonStyle="link"
          taskFor={person.task_for}
          nextTask={person.next_task}
          taskable={person.taskable}
        />
      </td>,
    ],
    [
      "primaryAgent",
      <td key="primaryAgent" className="primary_agent" aria-label="primary agent">
        <AgentOrPondSelect
          small
          triggerClasses="tw-bg-transparent tw-border-0 tw-flex tw-items-center tw-gap-[8px] tw-w-full tw-overflow-hidden tw-p-0 tw-pl-[10px]"
          nameClasses="tw-min-w-0 tw-overflow-hidden tw-text-left"
          caretClasses="tw-flex tw-items-center tw-justify-center"
          leadPond={leadPond}
          personId={person.person?.id}
          primaryAgent={primaryAgent}
          setLeadPond={setLeadPond}
          setPrimaryAgent={setPrimaryAgent}
        />
      </td>,
    ],
    [
      "autoPlans",
      <td key="autoPlans" className="action-plans text-center">
        <AssignedPlansPreviewApp
          assignedPlans={{
            assignableId: person.person.uuid,
            assignableType: "Person",
            assignableName: person.full_name,
            assignedPlansPath: person.assigned_plans_path,
            autoPlansPath: person.auto_plans_path,
            assignedPlansState: person.assigned_plans_state,
            platformAccount: platformAccounts,
          }}
        />
        {person.assigned_plans_activity_date && (
          <h6 className="lower-row tw-pr-[10px]">{person.assigned_plans_activity_date}</h6>
        )}
      </td>,
    ],
    [
      "birthdate",
      <td key="birthdate" className="med-width-col" aria-label="birthdate">
        <MilestoneDate milestoneDate={person.birthdate_milestone_date} />
      </td>,
    ],
    [
      "homeAnniversary",
      <td key="homeAnniversary" className="med-width-col" aria-label="anniversary">
        <MilestoneDate milestoneDate={person.anniversary_milestone_date} />
      </td>,
    ],
    [
      "homeApp",
      <td key="homeApp" className="home-app-link text-center" aria-label="home app link">
        <HomeAppLink
          inviteSent={person.person.home_app_invite_sent}
          lastLogin={person.person.home_app_last_login}
          slug={person.person.slug}
          personId={person.id}
          emailActive={person.email_active}
          smsActive={person.sms_active}
          recipientFirstName={person.person.first_name}
          recipientEmailAddress={person.person.main_email}
          teamName={account.teamName}
          currentUserFullName={currentUser.fullName}
        />
      </td>,
    ],
    [
      "hotScore",
      <td key="hotScore" className="stat">
        <h6 className="bolder">{person.person.hot_lead_score}</h6>
      </td>,
    ],
    [
      "lastVisit",
      <td key="lastVisit" className="stat last-visit-col">
        <h6 className="last-visit">{lastVisit(person)}</h6>
      </td>,
    ],
    [
      "visits",
      <td key="visits" className="stat loader">
        <h6 className="visits ">{Visits(person)}</h6>
        <h6 className="lower-row">
          <span>Visits</span>
        </h6>
      </td>,
    ],
    [
      "views",
      <td
        key="views"
        className={`stat loader ${hasIdxInfo(person.person) && "clickable"}`}
        aria-label="idx views"
      >
        <FavoritesOrViews
          count={person.person.idx_property_views}
          isFavorite={false}
          person={person.person}
          idxDataUri={idxDataUri}
          idxDataToken={idxDataToken}
        />
      </td>,
    ],
    [
      "avgViewPrice",
      <td
        key="avgViewPrice"
        className={`barely-wider-stat loader ${hasIdxInfo(person.person) && "clickable"}`}
      >
        <h6 className="price">
          {person.idx_avg_price && person.idx_avg_price !== 0
            ? person.number_to_currency_idx_avg_price
            : "N/A"}
        </h6>
      </td>,
    ],
    [
      "favorites",
      <td
        key="favorites"
        className={`stat loader ${hasIdxInfo(person.person) && "clickable"}`}
        aria-label="idx favorites"
      >
        <FavoritesOrViews
          count={person.person.idx_favorites}
          person={person.person}
          idxDataUri={idxDataUri}
          idxDataToken={idxDataToken}
          isFavorite
        />
      </td>,
    ],
    [
      "avgFavPrice",
      <td key="avgFavPrice" className={`stat loader ${hasIdxInfo(person.person) && "clickable"}`}>
        <h6 className="avg-favorites">
          {person.idx_average_price_favorited && person.idx_average_price_favorited !== 0
            ? person.number_to_currency_idx_average_price_favorited
            : "N/A"}
        </h6>
      </td>,
    ],
    [
      "listingAlerts",
      <td key="listingAlerts" className="stat loader alerts" aria-label="listing alerts">
        <AllListingAlertPreview
          person={person.person}
          emailDetails={person.primary_email_address}
          geoCodeAccountAddress={account?.address?.geocodeString}
          featureFlagPropertyCollections={featureFlags.propertyCollections}
          onToggleCollectionNotification={onToggleCollectionNotification}
          doesLeadHaveApp={!!person?.person?.home_app_last_login}
        />
      </td>,
    ],
    [
      "lastSent",
      <td key="lastSent" className="stat med-width-col">
        <h6>{person.last_sent_listing_alert}</h6>
      </td>,
    ],
    [
      "lastOpenedAlerts",
      <td key="lastOpenedAlerts" className="stat med-width-col">
        <h6>{person.last_opened_listing_alert}</h6>
      </td>,
    ],
    [
      "marketReports",
      <td key="marketReports" className="stat market-reports med-width-col" aria-label="market reports">
        <MarketReportPreview
          person={person.person_object}
          person_status={person.person_status}
          person_address={person.display_a_street_address}
          person_lead_type={person.person_lead_type}
          person_avatar={person.person_avatar}
          editReportFn={editMarketReport}
          deleteReportFn={(id) => deleteMarketReport(id, person?.primaryAgent?.uuid)}
          openMarketReport={() => setMarketModal(true)}
        />
      </td>,
    ],
    [
      "lastOpenedReports",
      <td key="lastOpenedReports" className="stat med-width-col">
        <h6>{person.last_opened_market_report}</h6>
      </td>,
    ],
    [
      "lastViewedReports",
      <td key="lastViewedReports" className="stat med-width-col">
        <h6>{person.last_viewed_market_report}</h6>
      </td>,
    ],
    [
      "source",
      <td key="source" className="source">
        <h6>{person.source}</h6>
        <h6>{person.referrer}</h6>
      </td>,
    ],
    [
      "registered",
      <td key="registered" className="submitted">
        <h6>{person.submitted_date}</h6>
        <h6>{person.submitted_time}</h6>
      </td>,
    ],
    [
      "transactionRole",
      <td key="transactionRole" className="tw-min-w-[140px] tw-w-[140px]">
        <h6 className="tw-ml-[30px] tw-justify-center tw-flex">{person.transaction_role}</h6>
      </td>,
    ],
    [
      "userType",
      <td key="userType" className="tw-min-w-[140px] tw-w-[140px]">
        <h6 className="tw-ml-[30px] tw-justify-center tw-flex">{person.person.user_type}</h6>
      </td>,
    ],
    [
      "tagIcon",
      <td key="tagIcon" className="icon" aria-label="person">
        <TagTooltip person={person} />
      </td>,
    ],
    [
      "cmaIcon",
      <td key="cmaIcon" className="icon" aria-label="cma link">
        <CMALink person={person} setShowModal={() => setMarketModal(true)} />
      </td>,
    ],
  ].filter(Boolean);
};

export default cellRenderers;
